import { useState, useEffect } from "react";
import { Bell, BellOff, Loader2, ChevronDown } from "lucide-react";
import { API, axios } from "@/api";
import { requestFCMToken, clearFCMToken, getNotificationPermission } from "@/lib/fcm";
import { toast } from "sonner";

const PREF_LABELS = {
  birthday_30d: { label: "30-day reminder", group: "Birthday Countdown" },
  birthday_21d: { label: "21-day reminder", group: "Birthday Countdown" },
  birthday_14d: { label: "14-day reminder", group: "Birthday Countdown" },
  birthday_7d: { label: "7-day reminder", group: "Birthday Countdown" },
  birthday_1d: { label: "Day-before reminder", group: "Birthday Countdown" },
  expiring_soon: { label: "Expiring deals (3 days)", group: "Deals" },
  new_deals_weekly: { label: "New deals weekly digest", group: "Deals" },
  deal_updated: { label: "Favorited deal updated", group: "Deals" },
  badge_earned: { label: "Badge earned", group: "Achievements" },
  level_up: { label: "Level up", group: "Achievements" },
  monthly_challenge: { label: "Monthly challenge", group: "Achievements" },
  streak_at_risk: { label: "Streak at risk", group: "Achievements" },
  whats_new: { label: "What's new announcements", group: "App Updates" },
  beta_updates: { label: "Beta program updates", group: "App Updates" },
};

const GROUPS = ["Birthday Countdown", "Deals", "Achievements", "App Updates"];

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [permission, setPermission] = useState(null);
  const [hasToken, setHasToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/notifications/prefs`);
        setPrefs(data.prefs);
        setPermission(data.permission);
        setHasToken(data.has_token);
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const masterEnabled = hasToken && permission !== "denied";

  const handleMasterToggle = async () => {
    if (masterEnabled) {
      setEnabling(true);
      try {
        await clearFCMToken();
        setHasToken(false);
        setPermission("denied");
        toast.success("Push notifications disabled");
      } catch {}
      finally { setEnabling(false); }
    } else {
      setEnabling(true);
      try {
        const token = await requestFCMToken();
        if (token) {
          setHasToken(true);
          setPermission("granted");
          toast.success("Push notifications enabled!");
        } else {
          toast.info("Notifications blocked by browser");
        }
      } catch {}
      finally { setEnabling(false); }
    }
  };

  const togglePref = async (key) => {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    setSaving(true);
    try { await axios.put(`${API}/notifications/prefs`, { notification_prefs: updated }); }
    catch { toast.error("Failed to save"); setPrefs(prefs); }
    finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-testid="notification-settings">
      <button onClick={() => setExpanded(!expanded)} className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition" data-testid="notification-settings-toggle">
        <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
          {masterEnabled ? <Bell className="w-5 h-5 text-amber-500" /> : <BellOff className="w-5 h-5 text-slate-400" />}
        </div>
        <div className="flex-1 text-left">
          <p className="text-sm font-medium text-slate-700">Push Notifications</p>
          <p className="text-xs text-slate-400">{masterEnabled ? "Enabled" : "Disabled"}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-4" data-testid="notification-prefs-panel">
          {/* Master toggle */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-600">Enable all push notifications</span>
            <button onClick={handleMasterToggle} disabled={enabling} className={`w-11 h-6 rounded-full transition-colors relative ${masterEnabled ? "bg-amber-500" : "bg-slate-200"}`} data-testid="notification-master-toggle">
              {enabling ? <Loader2 className="w-4 h-4 animate-spin absolute top-1 left-3.5 text-white" /> : <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-all ${masterEnabled ? "left-[22px]" : "left-0.5"}`} />}
            </button>
          </div>

          {masterEnabled && prefs && GROUPS.map(group => (
            <div key={group}>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">{group}</p>
              {Object.entries(PREF_LABELS).filter(([, v]) => v.group === group).map(([key, { label }]) => (
                <div key={key} className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-slate-600">{label}</span>
                  <button onClick={() => togglePref(key)} disabled={saving} className={`w-9 h-5 rounded-full transition-colors relative ${prefs[key] ? "bg-amber-400" : "bg-slate-200"}`} data-testid={`notif-pref-${key}`}>
                    <div className={`w-4 h-4 bg-white rounded-full shadow absolute top-0.5 transition-all ${prefs[key] ? "left-[18px]" : "left-0.5"}`} />
                  </button>
                </div>
              ))}
            </div>
          ))}

          {getNotificationPermission() === "denied" && (
            <p className="text-[10px] text-red-400 text-center">Notifications are blocked by your browser. Enable them in browser settings to receive push notifications.</p>
          )}
        </div>
      )}
    </div>
  );
}
